import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';

interface Store {
  id: string;
  user_id: string;
  store_name: string;
  store_description: string | null;
  logo_image: string | null;
  product_count: number;
  categories: string[];
}

export default function FeaturedStores() {
  const navigate = useNavigate();
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  useEffect(() => {
    fetchStores();
  }, []);

  const fetchStores = async () => {
    try {
      const { data: storeProfiles, error: storeError } = await supabase
        .from('store_profiles')
        .select('id, user_id, store_name, store_description, logo_image')
        .limit(8);

      if (storeError) throw storeError;

      if (!storeProfiles || storeProfiles.length === 0) {
        setStores([]);
        setLoading(false);
        return;
      }

      // Fetch products using user_id (not store_id)
      const userIds = storeProfiles.map(s => s.user_id);
      const { data: products, error: productsError } = await supabase
        .from('store_products')
        .select('user_id, category')
        .in('user_id', userIds);

      if (productsError) throw productsError;

      // Aggregate product data by user_id
      const storeMap: Record<string, { count: number; categories: Set<string> }> = {};
      (products || []).forEach((p) => {
        if (!storeMap[p.user_id]) {
          storeMap[p.user_id] = { count: 0, categories: new Set() };
        }
        storeMap[p.user_id].count += 1;
        if (p.category) storeMap[p.user_id].categories.add(p.category);
      });

      // Combine data and sort by product count
      const storesWithProducts = storeProfiles
        .map(store => ({
          id: store.id,
          user_id: store.user_id,
          store_name: store.store_name,
          store_description: store.store_description,
          logo_image: store.logo_image,
          product_count: storeMap[store.user_id]?.count || 0,
          categories: Array.from(storeMap[store.user_id]?.categories || []),
        }))
        .sort((a, b) => b.product_count - a.product_count)
        .slice(0, 8);

      setStores(storesWithProducts);
    } catch (error) {
      console.error('Error fetching stores:', error);
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((w) => w[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const gradients = [
    'from-violet-400 to-purple-300',
    'from-teal-400 to-cyan-300',
    'from-orange-400 to-red-300',
    'from-pink-400 to-rose-300',
    'from-emerald-400 to-green-300',
    'from-amber-400 to-orange-300',
    'from-blue-400 to-indigo-300',
    'from-fuchsia-400 to-pink-300',
  ];

  if (loading) {
    return (
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 sm:py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-10 sm:mb-14 gap-6">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-teal-50 text-teal-700 rounded-full mb-4">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-store-3-line text-sm"></i>
              </div>
              <span className="text-xs font-semibold tracking-wide uppercase">Digital Marketplace</span>
            </div>
            <h4 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              <a href="#featured-stores" className="hover:text-teal-600 transition-colors">
                Featured Creator Stores
              </a>
            </h4>
            <p className="text-sm sm:text-base text-gray-500 max-w-lg">
              Explore digital products from talented creators and event hosts
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 sm:gap-6" id="featured-stores">
          {stores.map((store, index) => (
            <div
              key={store.id}
              onClick={() => navigate(`/store/${store.user_id}`)}
              className="group relative bg-white rounded-xl border border-gray-100 p-4 sm:p-6 hover:shadow-xl hover:border-transparent hover:-translate-y-1 transition-all duration-300 cursor-pointer"
              onMouseEnter={() => setHoveredId(store.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center rounded-full bg-teal-50 text-teal-600">
                  <i className="ri-arrow-right-up-line text-xs sm:text-sm"></i>
                </div>
              </div>

              <div className="flex flex-col items-center text-center">
                <div className="relative mb-3 sm:mb-4">
                  <div
                    className={`w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden ring-3 ring-offset-2 transition-all duration-300 ${
                      hoveredId === store.id ? 'ring-teal-400 scale-105' : 'ring-gray-100'
                    }`}
                  >
                    {store.logo_image ? (
                      <img
                        src={store.logo_image}
                        alt={store.store_name}
                        className="w-full h-full object-cover object-top"
                      />
                    ) : (
                      <div
                        className={`w-full h-full bg-gradient-to-br ${gradients[index % gradients.length]} flex items-center justify-center`}
                      >
                        <span className="text-white text-lg sm:text-xl font-bold">
                          {getInitials(store.store_name)}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 sm:w-7 sm:h-7 bg-teal-500 rounded-full flex items-center justify-center border-2 border-white shadow-sm">
                    <i className="ri-store-3-fill text-white text-xs"></i>
                  </div>
                </div>

                <h3 className="text-xs sm:text-sm font-semibold text-gray-900 mb-1 group-hover:text-teal-600 transition-colors truncate w-full px-1">
                  {store.store_name}
                </h3>

                <div className="flex items-center gap-1.5 mb-2 sm:mb-3">
                  <div className="w-4 h-4 flex items-center justify-center text-teal-500">
                    <i className="ri-shopping-bag-line text-xs"></i>
                  </div>
                  <span className="text-xs text-gray-500 font-medium">
                    {store.product_count} {store.product_count === 1 ? 'Product' : 'Products'}
                  </span>
                </div>

                <div className="flex flex-wrap items-center justify-center gap-1 sm:gap-1.5">
                  {store.categories.slice(0, 2).map((cat) => (
                    <span
                      key={cat}
                      className="px-2 sm:px-2.5 py-0.5 bg-gray-50 text-gray-500 text-[10px] sm:text-[11px] font-medium rounded-full truncate max-w-full"
                    >
                      {cat}
                    </span>
                  ))}
                  {store.categories.length > 2 && (
                    <span className="px-1.5 sm:px-2 py-0.5 text-gray-400 text-[10px] sm:text-[11px] font-medium">
                      +{store.categories.length - 2}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}